import React from "react"
import {useTranslation} from "react-i18next"
import {
  BriefcaseIcon,
  ChatBubbleLeftIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CpuChipIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline"

import {TReachActivityChannel} from "../../queries/reachEngine"
import {AChannelsChannelEnum, AChannelsStatusEnum} from "../../services/types.generated"

// The API reports every call as one `call` channel, shown as human calls. Automated
// calls are a frontend-only channel with no data yet, so they are never in the flows.
export const AUTOMATED_CALL = "automated_call"

export type TReachChannel = AChannelsChannelEnum | typeof AUTOMATED_CALL

export type TReachChannelRow = Omit<TReachActivityChannel, "channel"> & {channel: TReachChannel}

// -----------------------------------------------------------------------------
// Per-channel colors — single source for the channel cards. Concrete rgb()
// strings (not `var(--…)`): these are consumed as SVG paint by Recharts, where a
// CSS var in a presentation attribute does not resolve. Values mirror design
// tokens in src/index.css.
// -----------------------------------------------------------------------------

export const CHANNEL_COLORS: Record<TReachChannel, string> = {
  [AChannelsChannelEnum.Email]: "rgb(0, 34, 238)", // --color-cr-blue
  [AChannelsChannelEnum.Call]: "rgb(15, 221, 221)", // --color-cr-cyan
  [AUTOMATED_CALL]: "rgb(72, 96, 241)", // --color-cr-blue-mid
  [AChannelsChannelEnum.Sms]: "rgb(72, 96, 241)", // --color-cr-blue-mid
  [AChannelsChannelEnum.Linkedin]: "rgb(72, 96, 241)", // --color-cr-blue-mid
  [AChannelsChannelEnum.Whatsapp]: "rgb(72, 96, 241)", // --color-cr-blue-mid
}

// Fixed render order so channels never reshuffle between date windows.
export const CHANNEL_ORDER: readonly TReachChannel[] = [
  AChannelsChannelEnum.Email,
  AChannelsChannelEnum.Call,
  AUTOMATED_CALL,
  AChannelsChannelEnum.Sms,
  AChannelsChannelEnum.Linkedin,
  AChannelsChannelEnum.Whatsapp,
]

// @heroicons has no brand marks for WhatsApp/LinkedIn, so each channel maps to its
// closest semantic glyph.
export const CHANNEL_ICONS: Record<TReachChannel, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  [AChannelsChannelEnum.Email]: EnvelopeIcon,
  [AChannelsChannelEnum.Call]: PhoneIcon,
  [AUTOMATED_CALL]: CpuChipIcon,
  [AChannelsChannelEnum.Whatsapp]: ChatBubbleOvalLeftEllipsisIcon,
  [AChannelsChannelEnum.Linkedin]: BriefcaseIcon,
  [AChannelsChannelEnum.Sms]: ChatBubbleLeftIcon,
}

// Literal keys, so the translation checker sees every channel label.
export function useChannelLabels(): Record<TReachChannel, string> {
  const {t} = useTranslation()

  return {
    [AChannelsChannelEnum.Email]: t("ReachEngine_Channel_Email"),
    [AChannelsChannelEnum.Call]: t("ReachEngine_Channel_HumanCall"),
    [AUTOMATED_CALL]: t("ReachEngine_Channel_AutomatedCall"),
    [AChannelsChannelEnum.Sms]: t("ReachEngine_Channel_Sms"),
    [AChannelsChannelEnum.Linkedin]: t("ReachEngine_Channel_Linkedin"),
    [AChannelsChannelEnum.Whatsapp]: t("ReachEngine_Channel_Whatsapp"),
  }
}

// One row per channel in CHANNEL_ORDER. A channel the API leaves out is treated as
// not part of the flows, so every card always shows all six channels. Automated
// calls are never in the flows, whatever the API returns.
export function buildChannelRows(channels: readonly TReachActivityChannel[]): TReachChannelRow[] {
  return CHANNEL_ORDER.map(
    channel =>
      (channel !== AUTOMATED_CALL && channels.find(row => row.channel === channel)) || {
        channel,
        status: AChannelsStatusEnum.NotInFlows,
        activities: 0,
        responses: 0,
      }
  )
}

// Channels outside the flows contribute nothing, whatever values they carry.
export function sumChannels(channels: readonly TReachChannelRow[]): {activities: number; responses: number} {
  return channels.reduce(
    (totals, row) =>
      row.status === AChannelsStatusEnum.NotInFlows
        ? totals
        : {activities: totals.activities + row.activities, responses: totals.responses + row.responses},
    {activities: 0, responses: 0}
  )
}

const STATUS_RANK: Record<AChannelsStatusEnum, number> = {
  [AChannelsStatusEnum.Active]: 0,
  [AChannelsStatusEnum.ActiveNoActivity]: 1,
  [AChannelsStatusEnum.NotInFlows]: 2,
}

// Active channels by responses (most first), then channels without activity, then
// channels outside the flows. Ties keep the fixed channel order.
export function sortChannelsForTable(channels: readonly TReachChannelRow[]): TReachChannelRow[] {
  return [...channels].sort(
    (a, b) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      (a.status === AChannelsStatusEnum.Active ? b.responses - a.responses : 0) ||
      CHANNEL_ORDER.indexOf(a.channel) - CHANNEL_ORDER.indexOf(b.channel)
  )
}
